// Uploads a file through the API, which validates it against its magic bytes,
// recompresses images and stores the result in S3.
//
// This replaces the old presigned-URL flow, in which the browser PUT the raw
// file straight to S3. That flow could not compress anything (the API never saw
// the bytes) and could not bound anything either — a presigned PUT constrains
// only what was signed, and the size was not among it.

export interface UploadedFile {
  /** S3 object key — store this where a key is expected. */
  key: string;
  /** Fetchable URL through the API's asset endpoint; the bucket itself is private. */
  file_url: string;
  /** May differ from the file's own type: an opaque PNG photo is stored as JPEG. */
  content_type: string;
  /** Measured server-side after compression — not the browser's claim. */
  size_bytes: number;
  original_size_bytes: number;
}

/**
 * The API's ceiling on the RAW upload (MAX_UPLOAD_BYTES in upload.service.ts).
 * Images are recompressed first and only then held to the stored-size cap, so an
 * image picker must not refuse a big photo on its raw size below this.
 */
export const MAX_RAW_UPLOAD_BYTES = 10 * 1024 * 1024;

export type UploadContext =
  | 'product-image'
  | 'category-image'
  | 'tenant-logo'
  | 'expense-receipt'
  | 'wastage-photo'
  | 'social-media'
  | 'document';

export async function uploadFile(
  file: File,
  context: UploadContext,
  fetchImpl: typeof fetch = fetch,
): Promise<UploadedFile> {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('context', context);

  // No Content-Type header — the browser must set the multipart boundary.
  const res = await fetchImpl('/api/v1/uploads/file', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!res.ok) {
    // The API explains *why* (wrong type, too large even after compression);
    // surfacing that beats a generic "upload failed".
    const body = await res.json().catch(() => null);
    throw new Error(body?.message ?? `Upload failed (${res.status})`);
  }

  const body = await res.json();
  return body?.data ?? body;
}
